import { Box, Divider, Grid, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import colors from '../../../../assets/theme/base/colors';
import { EFTargetPeriod, IEFTargetPeriod } from '../../../../Component/Model/Dto/EmpowermentFinancing';
import { formatRand, groupRandInput } from '../../../../Helpers/EmpowermentFinancing/formatHelper';
import { FieldLabel, SubHeading } from './TargetSettingsLabels';

interface IProps {
	targetPeriods: IEFTargetPeriod[];
	targetPeriodId: EFTargetPeriod;
	onTargetPeriodChange: (value: EFTargetPeriod) => void;
	periodYears: number[];
	applicableYear: number;
	onApplicableYearChange: (value: number) => void;
	financialYearEnd: string;
	onFinancialYearEndChange: (value: string) => void;
	monetaryTargetRaw: string;
	monetaryTarget: number;
	onMonetaryTargetChange: (value: string) => void;
}

export const ProgrammeEnvelopePanel = ({
	targetPeriods,
	targetPeriodId,
	onTargetPeriodChange,
	periodYears,
	applicableYear,
	onApplicableYearChange,
	financialYearEnd,
	onFinancialYearEndChange,
	monetaryTargetRaw,
	monetaryTarget,
	onMonetaryTargetChange,
}: IProps) => (
	<>
		<SubHeading sx={{ mb: 2 }}>Programme envelope</SubHeading>

		<FieldLabel>Applicable 5-Year Target Period</FieldLabel>
		<Select
			size="small"
			fullWidth
			value={targetPeriodId}
			onChange={e => onTargetPeriodChange(Number(e.target.value) as EFTargetPeriod)}
			sx={{ fontSize: '0.8125rem' }}
		>
			{targetPeriods.map(period => (
				<MenuItem key={period.id} value={period.id} sx={{ fontSize: '0.8125rem' }}>
					{period.name}
				</MenuItem>
			))}
		</Select>

		<Box sx={{ mt: 2.5 }}>
			<FieldLabel>Monetary Target Confirmed by BASA - One Target for 5-Year Period</FieldLabel>
			<Box
				sx={{
					border: '1px solid',
					borderColor: colors.ui.border,
					borderRadius: 1,
					bgcolor: colors.empowermentFinancing.sectionHeaderBackground,
					p: 2,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
					<Typography sx={{ fontSize: '1.875rem', fontWeight: 700, color: 'primary.main', lineHeight: 1.1 }}>
						{monetaryTarget > 0 ? formatRand(monetaryTarget) : '-'}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
							Edit value
						</Typography>
						<TextField
							size="small"
							value={monetaryTargetRaw}
							onChange={e => onMonetaryTargetChange(groupRandInput(e.target.value.replace(/[^0-9.]/g, '')))}
							placeholder="e.g. 7 892 374 987"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>R</Typography>
									</InputAdornment>
								),
							}}
							sx={{ width: 200, bgcolor: colors.white.main, borderRadius: 1, '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
						/>
					</Box>
				</Box>
				<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
					All annual and sub-targets derive from this figure.
				</Typography>
			</Box>
		</Box>

		<Divider sx={{ my: 3 }} />

		<SubHeading sx={{ mb: 2 }}>Annual derivation</SubHeading>
		<Grid container spacing={3} alignItems="flex-start">
			<Grid item xs={12} sm={6}>
				<FieldLabel>Applicable Year</FieldLabel>
				<Select
					size="small"
					fullWidth
					value={applicableYear}
					onChange={e => onApplicableYearChange(Number(e.target.value))}
					sx={{ fontSize: '0.8125rem' }}
				>
					{periodYears.map(year => (
						<MenuItem key={year} value={year} sx={{ fontSize: '0.8125rem' }}>
							{year}
						</MenuItem>
					))}
				</Select>
			</Grid>
			<Grid item xs={12} sm={6}>
				<FieldLabel>Financial Year-End</FieldLabel>
				<TextField
					size="small"
					fullWidth
					type="date"
					value={financialYearEnd}
					onChange={e => onFinancialYearEndChange(e.target.value)}
					sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
				/>
				<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
					Used in deployment schedule calculations
				</Typography>
			</Grid>
		</Grid>
	</>
);
